import {
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Text,
  makeStyles,
  tokens,
} from '@coreai/fluentui-react';
import {
  OrganizationHorizontalRegular,
  PlayRegular,
  PlugConnectedRegular,
} from '@fluentui/react-icons';

export type ToolType = 'action' | 'mcp' | 'subworkflow';

export interface ToolTypeMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onSelect: (type: ToolType) => void;
  onClose: () => void;
}

type ToolOption = {
  type: ToolType;
  label: string;
  description: string;
  icon: JSX.Element;
};

const TOOL_OPTIONS: ToolOption[] = [
  {
    type: 'action',
    label: 'Action',
    description: 'Add a built-in or connector action as a tool',
    icon: <PlayRegular />,
  },
  {
    type: 'mcp',
    label: 'MCP Tool',
    description: 'Connect a tool exposed by an MCP server',
    icon: <PlugConnectedRegular />,
  },
  {
    type: 'subworkflow',
    label: 'Sub-workflow',
    description: 'Invoke another workflow as a tool',
    icon: <OrganizationHorizontalRegular />,
  },
];

const useStyles = makeStyles({
  hiddenTrigger: {
    display: 'none',
  },
  item: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    columnGap: tokens.spacingHorizontalS,
    alignItems: 'start',
    paddingBlock: tokens.spacingVerticalXS,
  },
  textColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  description: {
    color: tokens.colorNeutralForeground3,
  },
});

export const ToolTypeMenu = ({ open, anchorEl, onSelect, onClose }: ToolTypeMenuProps) => {
  const styles = useStyles();

  if (!anchorEl) return null;

  return (
    <Menu
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onClose();
      }}
      positioning={{ target: anchorEl, position: 'below', align: 'start' }}
    >
      <MenuTrigger disableButtonEnhancement>
        <span className={styles.hiddenTrigger} />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {TOOL_OPTIONS.map((option) => (
            <MenuItem
              key={option.type}
              icon={option.icon}
              onClick={() => onSelect(option.type)}
              className={styles.item}
            >
              <div className={styles.textColumn}>
                <Text weight="semibold">{option.label}</Text>
                <Text size={200} className={styles.description}>
                  {option.description}
                </Text>
              </div>
            </MenuItem>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};
